import { PAPER_BLOCK_MINUTES, PAPER_WRITING_CRITERIA } from "../../../shared/certyfikat";

/**
 * Facts the posts read off the library, written down once.
 *
 * Same rule as `LANDING_NUMBERS`: `content/` may not be imported from `src/`, so
 * a figure counted over the real papers is typed here and `tests/blog.test.ts`
 * counts it again and fails the day a new sitting makes it untrue. A post never
 * types such a figure into its text — it interpolates it from here.
 *
 * Figures from `shared/` (telc's track, the thresholds, the word ranges) are not
 * repeated here: a post imports them from `shared/` directly.
 */
export const BLOG_FACTS = {
  /**
   * How often each listening task of a state-exam paper is played, as its own
   * instruction says ("Nagranie zostanie odtworzone tylko jeden raz" / "dwa razy",
   * "dwukrotnie"). On every B1 paper in the library the first task is played once
   * and every other task that states it, twice; on B2, every task that states it
   * is played twice.
   */
  stateListeningPlays: {
    B1: { tasks: 5, playedOnce: [1] },
    B2: { tasks: 5, playedOnce: [] as number[] },
  },
  /**
   * The silence between the two plays of a task, rounded to a second: the
   * median of every such pause `audioPauses` measured on the sittings' own
   * recordings (94 of them on 2026-10-03, nearly all 45.3–46.5 s). The board
   * prints no figure for it, so a post says "about" and that it was measured.
   */
  stateBetweenPasses: 46,
  /**
   * The story ("opowiadanie") as a long writing text. Every paper offers three
   * sets of two texts; the long one is text b. Counted over the briefs: how many
   * papers offer a story, and what the brief hands the candidate to start from —
   * a title, a first sentence, a photograph (one B1 brief gives a title and a
   * photograph, counted under `titleAndPhoto`).
   */
  stateStory: {
    B1: {
      papers: 14,
      longTexts: 42,
      papersWithStory: 8,
      prompts: { title: 2, titleAndPhoto: 1, firstSentence: 4, photo: 1 },
      words: { min: 170, max: 175 },
    },
    B2: {
      papers: 9,
      longTexts: 27,
      papersWithStory: 4,
      prompts: { title: 0, titleAndPhoto: 0, firstSentence: 0, photo: 4 },
      words: { min: 210, max: 270 },
    },
  },
  /**
   * The short text of the writing block (text a of each of a paper's three
   * sets — the candidate writes one set). Counted over the briefs: on how many
   * papers a set asks for an announcement ("napisać ogłoszenie") or an
   * invitation ("napisać zaproszenie", "zaprosić"), the words those briefs ask
   * for, and what the B1 announcements are about — selling something, a lost
   * pet or thing, looking for someone, an event.
   */
  stateShortForms: {
    B1: {
      papers: 14,
      shortTexts: 42,
      announcement: { papers: 14, words: { min: 25, max: 50 }, about: { sell: 6, lost: 3, wanted: 4, event: 1 } },
      invitation: { papers: 11, words: { min: 30, max: 50 } },
    },
    B2: {
      papers: 9,
      shortTexts: 27,
      announcement: { papers: 2, words: { min: 40, max: 50 } },
      invitation: { papers: 2, words: { min: 30, max: 30 } },
    },
  },
  /**
   * The B1 grammar block ("Poprawność gramatyczna"): the same eight tasks, in
   * the same order, with the same number of items and points, on every B1 paper
   * in the library. `topic` is what the task's instruction names in brackets
   * (or, for 5 and 6, what it asks you to do); `points` is the task's share of
   * the block's 30. `form`: pick a form in brackets, fill from a box with one
   * word too many, or write the form yourself. `variants` counts the papers per
   * wording where it moves (task 8 is a box on most papers, a choice on some).
   */
  /**
   * Complaints — "skarga" or "reklamacja" in a writing brief, on the past
   * papers. None at B1; at B2 one task each on three papers.
   */
  stateComplaints: {
    B1: { papers: 14, complaints: 0 },
    B2: { papers: 9, complaints: 3, skarga: 2, reklamacja: 1, words: { min: 130, max: 150 } },
  },
  /**
   * Every writing task on the past papers, sorted by the form its brief asks
   * for (`writingFormat`, `formats.ts`): on how many papers each form is set,
   * how often, as the short or the long text, and the lengths printed.
   */
  stateFormats: {
    B1: {
      papers: 14,
      tasks: 84,
      forms: {
        charakterystyka: { papers: 5, tasks: 5, short: 0, long: 5, words: { min: 160, max: 175 } },
        emailPrywatny: { papers: 8, tasks: 8, short: 0, long: 8, words: { min: 150, max: 175 } },
        esej: { papers: 6, tasks: 6, short: 0, long: 6, words: { min: 160, max: 170 } },
        listFormalny: { papers: 4, tasks: 4, short: 2, long: 2, words: { min: 50, max: 170 } },
        ogloszenie: { papers: 14, tasks: 14, short: 14, long: 0, words: { min: 25, max: 50 } },
        opis: { papers: 3, tasks: 3, short: 0, long: 3, words: { min: 170, max: 175 } },
        opowiadanie: { papers: 8, tasks: 8, short: 0, long: 8, words: { min: 170, max: 175 } },
        pozdrowienia: { papers: 9, tasks: 9, short: 9, long: 0, words: { min: 25, max: 40 } },
        recenzja: { papers: 4, tasks: 4, short: 0, long: 4, words: { min: 140, max: 175 } },
        sprawozdanie: { papers: 6, tasks: 6, short: 0, long: 6, words: { min: 150, max: 175 } },
        zaproszenie: { papers: 11, tasks: 11, short: 11, long: 0, words: { min: 30, max: 50 } },
        zyczenia: { papers: 6, tasks: 6, short: 6, long: 0, words: { min: 25, max: 40 } },
      },
    },
    B2: {
      papers: 9,
      tasks: 54,
      forms: {
        charakterystyka: { papers: 2, tasks: 2, short: 2, long: 0, words: { min: 150, max: 150 } },
        emailPrywatny: { papers: 5, tasks: 5, short: 3, long: 2, words: { min: 40, max: 150 } },
        esej: { papers: 4, tasks: 4, short: 0, long: 4, words: { min: 250, max: 270 } },
        listFormalny: { papers: 3, tasks: 3, short: 2, long: 1, words: { min: 50, max: 160 } },
        motywacyjny: { papers: 3, tasks: 3, short: 3, long: 0, words: { min: 90, max: 150 } },
        ogloszenie: { papers: 2, tasks: 2, short: 2, long: 0, words: { min: 40, max: 50 } },
        opis: { papers: 1, tasks: 1, short: 1, long: 0, words: { min: 140, max: 140 } },
        opowiadanie: { papers: 4, tasks: 4, short: 0, long: 4, words: { min: 210, max: 270 } },
        pozdrowienia: { papers: 3, tasks: 3, short: 3, long: 0, words: { min: 30, max: 50 } },
        recenzja: { papers: 4, tasks: 4, short: 1, long: 3, words: { min: 150, max: 200 } },
        reklama: { papers: 3, tasks: 3, short: 3, long: 0, words: { min: 50, max: 60 } },
        rozprawka: { papers: 8, tasks: 8, short: 0, long: 8, words: { min: 250, max: 270 } },
        skarga: { papers: 3, tasks: 3, short: 2, long: 1, words: { min: 130, max: 150 } },
        sprawozdanie: { papers: 4, tasks: 4, short: 0, long: 4, words: { min: 150, max: 180 } },
        zaproszenie: { papers: 2, tasks: 2, short: 2, long: 0, words: { min: 30, max: 30 } },
        zyczenia: { papers: 3, tasks: 3, short: 3, long: 0, words: { min: 30, max: 40 } },
      },
    },
  },
  stateGrammarB1: {
    papers: 14,
    tasks: [
      { topic: "declension", form: "choose", items: 10, points: 5 },
      { topic: "conjunctions", form: "bank", items: 5, points: 2.5 },
      { topic: "comparison", form: "choose", items: 5, points: 2.5 },
      { topic: "tenses", form: "write", items: 10, points: 5, variants: { presentFuture: 6, presentPast: 8 } },
      { topic: "questions", form: "write", items: 5, points: 2.5 },
      { topic: "transformation", form: "write", items: 5, points: 5 },
      { topic: "aspectMood", form: "choose", items: 10, points: 5 },
      { topic: "prepositions", form: "bank", items: 5, points: 2.5, variants: { bank: 11, choice: 3 } },
    ],
  },
  /**
   * The past tense and the conditional inside the B1 grammar part, counted
   * over the answers. `past`: on the papers whose task 4 is "present and past",
   * how many gaps the answer to is a past form, and of those how many are
   * plural (-li / -ły — the masculine-personal choice) and how many are the
   * first or second person singular (-łem / -łam, -łeś / -łaś — the speaker's
   * gender). `mood`: task 7, "aspekt i tryby" — how many answers are a
   * conditional form, in how many gaps one is offered at all, and on how many
   * papers the text has "gdyby".
   */
  stateVerbsB1: {
    past: { papers: 8, gaps: 80, answers: 41, plural: 21, firstSecond: 7 },
    mood: { gaps: 140, conditional: 51, offered: 134, gdybyPapers: 12 },
  },
  /**
   * "BI" — brak informacji — the third answer of a TAK/NIE/BI task. Counted
   * over the papers: which carry such a task (the instruction names BI and the
   * answers use it), where, how many statements and what each is worth, and
   * how the published answers split between TAK, NIE and BI.
   */
  stateBi: {
    B1: { papers: 14, withBi: 0 },
    B2: {
      papers: 9,
      withBi: 6,
      module: "reading" as const,
      statementsPerTask: 10,
      pointsPerStatement: 1,
      keys: { tak: 22, nie: 20, bi: 18 },
      biPerTask: { min: 2, max: 4 },
    },
  },
  /** The whole listening recording, shortest and longest, in seconds. */
  stateListeningSeconds: {
    B1: { min: 1545, max: 1690 },
    B2: { min: 1881, max: 2378 },
  },
} as const;

/**
 * What the law says happens after a state-exam result, read on the day in
 * `checked` — external, so no test can hold it; the post prints the date.
 *
 * - Rozporządzenie MNiSW z 14 lutego 2025 r. (Dz.U. 2025 poz. 217): §22 ust. 3
 *   (each module's result in percent), §23 (the pass rule), §25 (the result in
 *   writing within 3 days of the protocol; a copy of the work on request within
 *   21 days), §26 (re-marking), §27 (certificate within 30 days of the protocol).
 *   It provides no retake of one part and no carrying over of a passed module.
 * - Ustawa o języku polskim, art. 11h ust. 4, as quoted on certyfikatpolski.pl
 *   "Procedury": a request to re-mark the *written* part within 21 days, 20 euro;
 *   the Commission decides within 30 days, at most 60, and holds the certificate.
 * - certyfikatpolski.pl FAQ: no waiting period after a fail; the fee is the
 *   centre's, capped at 150 euro for adult B1 and B2; the certificate never expires.
 */
export const STATE_AFTER_RESULT = {
  checked: "2026-10-03",
  resultDaysAfterProtocol: 3,
  viewCopyDays: 21,
  remarkDays: 21,
  remarkFeeEur: 20,
  remarkDecisionDays: 30,
  remarkDecisionMaxDays: 60,
  certificateDays: 30,
  feeCapEurAdultsB1B2: 150,
  sources: {
    regulation: "https://dziennikustaw.gov.pl/DU/2025/217",
    procedures: "https://certyfikatpolski.pl/o-egzaminie/procedury/",
    faq: "https://certyfikatpolski.pl/najczesciej-zadawane-pytania/",
  },
} as const;

/**
 * What every adult paper prints over its writing block, read off the 24 PDFs in
 * `.sources/certyfikat/` (which the tests cannot read — the folder is not in
 * git), on the day in `checked`. The criteria cover the block, both texts
 * together; how the points split between the two texts is not printed.
 */
export { PAPER_BLOCK_MINUTES };
export const STATE_WRITING_PRINTED = {
  checked: "2026-10-03",
  B1: { minutes: PAPER_BLOCK_MINUTES.B1.writing, criteria: PAPER_WRITING_CRITERIA.B1 },
  B2: { minutes: PAPER_BLOCK_MINUTES.B2.writing, criteria: PAPER_WRITING_CRITERIA.B2 },
} as const;

/**
 * The rules of the day itself, and of the certificate afterwards, as the
 * examining bodies publish them — read on `checked`, so a post prints the date.
 *
 * State exam: Rozporządzenie MNiSW z 14 lutego 2025 r. (§6 rooms, §12 what may
 * not be brought in and the identity check, §15 the paper is signed, §16 a break
 * of at least ten minutes between written modules and leaving early, §17 the oral
 * after the written part the same or the next day, drawn from a set of tasks, §19
 * annulment; §8 pkt 5 and §9 ust. 3: the oral criteria reach the commission in a
 * sealed envelope); the Commission's "Wskazówki dla zdających" (photo ID, no
 * pencil, no correction fluid, in one at a time) and FAQ ("ważny bezterminowo").
 *
 * telc: Rules and Regulations for telc Examinations, valid from 15 April 2025
 * (Examination Regulations §4 photo ID and late arrival, §5 what may be brought
 * in, §7 no validity period, retakes, duplicates for ten years, §8 appeals within
 * six weeks, viewing within three months; Appendix 1 no partial credit for a
 * two-level exam; the glossary's "hybrid examination"), the telc FAQ, and the
 * Polish B1·B2 page ("available as a hybrid and paper-based exam").
 */
export const EXAM_RULES = {
  checked: "2026-10-03",
  state: {
    breakMinutes: 10,
    certificateDays: 30,
    dataYearsWithCertificate: 25,
    sources: {
      regulation: "https://dziennikustaw.gov.pl/DU/2025/217",
      tips: "https://certyfikatpolski.pl/o-egzaminie/wskazowki-dla-zdajacych/",
      faq: "https://certyfikatpolski.pl/najczesciej-zadawane-pytania/",
      withoutExam: "https://certyfikatpolski.pl/wydawanie-certyfikatu-bez-egzaminu/podstawa-prawna/",
      centres: "https://certyfikatpolski.pl/lista-podmiotow-uprawnionych-do-organizowania-egzaminow-certyfikatowych-z-jezyka-polskiego-jako-obcego/",
    },
  },
  telc: {
    writtenOralMaxDaysApart: 7,
    appealWeeks: 6,
    viewingMonths: 3,
    archiveYears: 10,
    correctionMonths: 4,
    sources: {
      rules: "https://www.telc.net/fileadmin/user_upload/pdfs/AGB_Pruefungsordnung/10000311_9994-P00-150011.pdf",
      faq: "https://www.telc.net/en/language-examinations/language-examinations-support-faq/",
      exam: "https://www.telc.net/en/language-examinations/certificate-exams/polish/telc-polish-b1b2-school/",
    },
  },
} as const;

/** `1545` → `"25:45"`. */
export const mmss = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;

/**
 * What the standards in the annex to the regulation (Dz.U. 2025 poz. 217, part
 * II — adults) ask of each level, read on the day in `checked`. External, so no
 * test can hold it; the post prints the date. `forms` is section 4.2 ("Formy
 * wypowiedzi") word for word, in its order; `spelling` counts the points of
 * spelling and punctuation in 4.1. The B2 list's footnotes narrow "podanie" to
 * the body of the letter and "list urzędowy" to an official letter to a person
 * or an institution.
 */
export const LEVEL_STANDARDS = {
  checked: "2026-10-17",
  source: "https://dziennikustaw.gov.pl/DU/2025/217",
  forms: {
    A2: ["życzenia", "pozdrowienia", "sms", "list prywatny nieformalny", "opis osoby", "opis przedmiotu", "opis miejsca", "opis sytuacji", "charakterystyka osoby", "wypracowanie"],
    B1: ["życzenia", "pozdrowienia", "zaproszenie", "zawiadomienie", "ogłoszenie", "list prywatny formalny", "list prywatny nieformalny", "opis osoby", "opis przedmiotu", "opis miejsca", "opis sytuacji", "charakterystyka osoby", "opowiadanie", "sprawozdanie", "recenzja", "esej"],
    B2: ["życzenia", "pozdrowienia", "zaproszenie", "zawiadomienie", "ogłoszenie", "list prywatny formalny", "list prywatny nieformalny", "opis osoby", "opis przedmiotu", "opis miejsca", "opis krajobrazu", "opis sytuacji", "charakterystyka", "opowiadanie", "sprawozdanie", "recenzja", "esej", "podanie", "(auto)biografia", "list motywacyjny", "list urzędowy", "reklama", "rozprawka"],
  },
  spelling: { A2: 3, B1: 5, B2: 11 },
} as const;

/**
 * The past tense and the conditional in the lists of grammar points of the
 * same annex, part II (adults), section 2.2.1 "koniugacja" and 2.2.3
 * "składnia" — the examples are the regulation's own, word for word. Read off
 * the PDF on the day in `checked`. External: the posts print the date.
 * A1 has no conditional; "gdyby …, to …" enters the conjunctions at B1, and
 * the three conditional periods (real, possible, unreal) are named at B2.
 */
export const VERB_POINTS = {
  checked: "2026-11-03",
  source: LEVEL_STANDARDS.source,
  past: {
    A1: "słuchałem, napisałaś, zrobiliśmy, umyłyśmy",
    A2: "leżały, kupowali, poszłam, zjadłeś, umyło się",
    B1: "leżały, kupowali, poszłam, zjadłeś, umyło się",
    B2: "zaczęły, ciął, urósł",
  },
  conditional: {
    A2: "chciałbym, oglądałby, kupiłyby, zrobilibyście",
    B1: "napisalibyście, przeczytałbyś",
    B2: "przyniósłbym, zaczęlibyśmy, wzięłabym, mógłbyś",
  },
  /** B2, "okresy warunkowe — nierzeczywiste": the regulation's own example. */
  unrealB2: "Gdybym wiosną więcej pracował, skończyłbym już pracę magisterską",
} as const;

/** The forms a level adds to the one below it, in the list's own order. */
export const newForms = (level: "B1" | "B2") => {
  const below = new Set<string>(LEVEL_STANDARDS.forms[level === "B1" ? "A2" : "B1"]);
  return LEVEL_STANDARDS.forms[level].filter((f) => !below.has(f));
};

/**
 * Karta Polaka and the step after it, read on the day in `checked`. External, so
 * no test can hold it; the post prints the date.
 *
 * Ustawa o Karcie Polaka, tekst jednolity Dz.U. 2026 poz. 76: art. 2 ust. 1 pkt 1
 * ("przynajmniej podstawowa znajomość języka polskiego"), art. 2 ust. 2 and
 * art. 19 pkt 4 (not for a holder of citizenship or a permanent residence
 * permit), art. 6 (rights), art. 7 (not citizenship, not a right to settle),
 * art. 12 ust. 3 (free), art. 13 ust. 7-8 (the documents that prove the language,
 * otherwise the consul or the voivode judges it in a conversation), art. 17
 * (ten years; indefinite from 65), art. 19a (30 + 30 days), art. 20 ust. 1a
 * (lapses with a permanent residence permit), art. 21 (appeal within 30 days).
 * Rozporządzenie RM Dz.U. 2017 poz. 1900, as amended by 2022 poz. 1498: all
 * sixteen voivodes take applications from citizens of Ukraine, Belarus and
 * Russia. Ustawa o obywatelstwie polskim, Dz.U. 2025 poz. 1611: art. 30 ust. 1
 * pkt 7 (one year on a permanent residence permit granted for the Karta Polaka)
 * and ust. 2 (a state certificate at B1 at least, or a school certificate).
 * Sejm, term 10: no bill amending the Karta Polaka act; prints 1273, 1759 and
 * 1888 amend the citizenship act and none had passed.
 */
export const KARTA_POLAKA = {
  checked: "2026-10-28",
  validYears: 10,
  indefiniteFromAge: 65,
  securityDays: 30,
  decisionDays: 30,
  appealDays: 30,
  railDiscountPercent: 37,
  voivodes: 16,
  citizenshipAfterYears: 1,
  citizenshipLevel: "B1",
  sources: {
    act: "https://api.sejm.gov.pl/eli/acts/DU/2026/76/text.pdf",
    voivodes: "https://api.sejm.gov.pl/eli/acts/DU/2022/1498/text.html",
    citizenship: "https://api.sejm.gov.pl/eli/acts/DU/2025/1611/text.pdf",
    consulate: "https://www.gov.pl/web/ukraina/karta-polaka-informacje-ogolne",
    voivode: "https://migrant.poznan.uw.gov.pl/pl/procedury/przyznanie-karty-polaka",
  },
} as const;

/**
 * The two ways to Polish citizenship for a foreigner, read on the day in
 * `checked`. External, so no test can hold it; the post prints the date.
 *
 * Ustawa o obywatelstwie polskim, tekst jednolity Dz.U. 2025 poz. 1611: art. 18-21
 * (the President "może nadać", no conditions; the application states the
 * applicant's Polish, art. 20 ust. 1 pkt 6; filed through a voivode or a consul),
 * art. 30 ust. 1 (recognition: the routes and their years below; `years` are
 * years of uninterrupted residence on a permanent residence permit, an EU
 * long-term resident permit or a right of permanent residence — the refugee and
 * Karta Polaka routes on the permanent residence permit granted for that reason;
 * the ten-year route counts all legal residence and needs the permit now),
 * art. 30 ust. 2-2a (Polish: a state certificate at B1 at least, or a school
 * certificate, not a post-secondary one), art. 31, art. 34 (to the voivode).
 * Ustawa o opłacie skarbowej, tekst jednolity Dz.U. 2026 poz. 1395, załącznik
 * część I pkt 26 and 27a. Sejm, term 10: prints 1273 and 1759 change art. 30
 * ust. 1 pkt 1 from three years to ten; print 1888 rewrites art. 30 (ten years,
 * five with a spouse, five after the Karta Polaka, C1, and a "Państwowy Egzamin
 * Obywatelski"); none had passed.
 */
export const CITIZENSHIP = {
  checked: "2026-10-28",
  routes: [
    { id: "residence", point: 1, years: 3, income: true },
    { id: "spouse", point: 2, years: 2, income: false },
    { id: "refugee", point: 3, years: 2, income: false },
    { id: "long", point: 6, years: 10, income: true },
    { id: "kartaPolaka", point: 7, years: 1, income: false },
  ],
  marriageYears: 3,
  level: "B1",
  feeRecognition: 1000,
  feePresident: 1669,
  bills: { years: 10, proposedLevel: "C1" },
  sources: {
    act: "https://api.sejm.gov.pl/eli/acts/DU/2025/1611/text.pdf",
    fees: "https://api.sejm.gov.pl/eli/acts/DU/2026/1395/text.pdf",
    bill1759: "https://api.sejm.gov.pl/sejm/term10/prints/1759/1759-ustawa.docx",
    bill1888: "https://api.sejm.gov.pl/sejm/term10/prints/1888/1888.pdf",
  },
} as const;

/**
 * The C1 state exam, which the library does not carry. The Commission's
 * past-papers page, read on `checked` in a browser (it refuses a plain fetch),
 * lists C1 papers from these sittings and C2 from three; the times, the parts
 * and the pass rule come from `CERTYFIKAT_VARIANTS` and the regulation (§14,
 * §15, §23), the dates from `SITTINGS`.
 */
export const C1_FACTS = {
  checked: "2026-10-28",
  publishedC1: ["2021-05-08", "2021-06-19", "2022-03-26", "2022-11-05", "2023-04-15", "2023-11-18", "2024-04-20"],
  publishedC2: 3,
} as const;

/**
 * What stands in for the B1 certificate, read on `checked` off the two acts.
 * Ustawa o obywatelstwie polskim (Dz.U. 2025 poz. 1611) art. 30 ust. 2, 2a:
 * the state certificate, or a school certificate from Poland or from a school
 * abroad taught in Polish; not a szkoła policealna; minors under ust. 1 pkt 4-5
 * are exempt. Ustawa o cudzoziemcach (Dz.U. 2025 poz. 1079) art. 211 ust. 1, 3, 4:
 * the long-term EU resident's permit after five years, a B1 certificate from the
 * minister's list, a school certificate or a degree taught in Polish, from
 * Poland or abroad, again not a szkoła policealna; nobody under sixteen. The
 * policealna rule took effect on 1 July 2025; such certificates gained by
 * 30 June 2025 counted for cases opened by 30 June 2026 (art. 15 and art. 30 of
 * the amending act, Dz.U. 2025 poz. 622, quoted in both notices).
 */
export const LANGUAGE_PROOF = {
  checked: "2026-10-29",
  level: "B1",
  residentYears: 5,
  residentMinAge: 16,
  policealnaFrom: "2025-07-01",
  transitionalUntil: "2026-06-30",
  sources: {
    citizenship: "https://api.sejm.gov.pl/eli/acts/DU/2025/1611/text.pdf",
    foreigners: "https://api.sejm.gov.pl/eli/acts/DU/2025/1079/text.pdf",
  },
} as const;

/**
 * The EU long-term resident's permit, read on `checked` off the Act on
 * Foreigners (Dz.U. 2025 poz. 1079): art. 211 (five years, income over the
 * last three — two for a Blue Card holder —, insurance, Polish), art. 212
 * (a studies permit counts by half; no break over six months, ten in all),
 * art. 223 with art. 202 and 210 (filed in person by the last day of legal
 * stay, a decision in six months, appeal in 90 days), art. 243 (the card is
 * valid five years), art. 215 (withdrawn after six years outside Poland or
 * twelve months in a row outside the EU); the fee from the stamp duty act
 * (Dz.U. 2026 poz. 1395), part III item 4.
 */
export const RESIDENT_EU = {
  checked: "2026-10-29",
  years: 5,
  incomeYears: 3,
  incomeYearsBlueCard: 2,
  breakMonths: 6,
  breaksTotalMonths: 10,
  decisionMonths: 6,
  appealDays: 90,
  cardYears: 5,
  fee: 640,
  awayFromPolandYears: 6,
  awayFromEuMonths: 12,
  sources: {
    act: "https://api.sejm.gov.pl/eli/acts/DU/2025/1079/text.pdf",
    fees: "https://api.sejm.gov.pl/eli/acts/DU/2026/1395/text.pdf",
  },
} as const;
